import { type ReactNode, type Ref, useId } from 'react';
import * as RadixSwitch from '@radix-ui/react-switch';
import classNames from 'classnames';

import styles from './Switch.module.scss';

export interface SwitchProps {
    checked?: boolean;
    /** Uncontrolled starting state, for callers that only listen. */
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    disabled?: boolean;
    /** Visible label, which also names the switch. Without one, pass `aria-label`. */
    label?: ReactNode;
    labelPosition?: 'left' | 'right';
    id?: string;
    name?: string;
    value?: string;
    required?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
    /** On the label row when there is a label, otherwise on the switch itself. */
    className?: string;
    ref?: Ref<HTMLButtonElement>;
}

/**
 * The portal's on/off switch: a Radix button with `role="switch"`, toggled by click or Space.
 * For one choice out of several, use SegmentedControl instead.
 */
export function Switch(props: SwitchProps) {
    const {
        ref,
        checked,
        defaultChecked,
        onCheckedChange,
        disabled,
        label,
        labelPosition = 'right',
        id,
        name,
        value,
        required,
        className,
    } = props;
    // useId's colons are valid in an id but not in a CSS selector, and tests select by id.
    const generatedId = useId().replace(/:/g, '');
    const hasLabel = label !== undefined && label !== null && label !== '';
    const switchId = id ?? (hasLabel ? generatedId : undefined);

    const switchElement = (
        <RadixSwitch.Root
            ref={ref}
            id={switchId}
            name={name}
            value={value}
            required={required}
            checked={checked}
            defaultChecked={defaultChecked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
            aria-describedby={props['aria-describedby']}
            className={classNames(styles.switchRoot, !hasLabel && className)}>
            <RadixSwitch.Thumb className={styles.switchThumb} />
        </RadixSwitch.Root>
    );

    if (!hasLabel) {
        return switchElement;
    }

    const labelElement = (
        <label
            htmlFor={switchId}
            className={classNames(styles.label, disabled && styles.labelDisabled)}>
            {label}
        </label>
    );

    return (
        <div className={classNames(styles.container, className)}>
            {labelPosition === 'left' && labelElement}
            {switchElement}
            {labelPosition === 'right' && labelElement}
        </div>
    );
}

export default Switch;
